import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  TextField,
  FormControl,
  ListItemSecondaryAction,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
} from '@material-ui/core';
import { TwitterPicker } from 'react-color';

import colors from '../../../../../../../utils/colors';

import styles from './styles';

const useStyles = makeStyles(styles);

export const FormInformationComponent = (props) => {
  const {
    formRequest,
    setFormRequest,
  } = props;
  const classes = useStyles();

  const [displayColorPicker, setDisplayColorPicker] = useState(false);

  const validateString = (title, nullable, minLength, maxLength) => {
    if (nullable && !title) {
      return true;
    }
    return !!title
        && title.trim().length >= minLength
        && title.trim().length <= maxLength;
  };

  const handleTitleChange = (e, forceSetChanged) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, false, 2, 100);
    setFormRequest({
      ...formRequest,
      title: changedValue,
      validation: {
        ...formRequest.validation,
        title: {
          hasChanged: formRequest.validation.title.hasChanged || changedValue.trim().length > 1 || forceSetChanged,
          isValid,
          message: formRequest.validation.title.message,
        },
      },
    });
  };

  const handleDescriptionChange = (e) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, true, 0, 1024);
    setFormRequest({
      ...formRequest,
      description: changedValue,
      validation: {
        ...formRequest.validation,
        description: {
          isValid,
          message: formRequest.validation.description.message,
        },
      },
    });
  };

  const handleSubmitButtonTextChange = (e) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, true, 0, 100);
    setFormRequest({
      ...formRequest,
      submitButtonText: changedValue,
      validation: {
        ...formRequest.validation,
        submitButtonText: {
          isValid,
          message: formRequest.validation.submitButtonText.message,
        },
      },
    });
    return isValid;
  };

  const handleSubmitLabelChange = (e) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, true, 0, 1024);
    setFormRequest({
      ...formRequest,
      submitLabel: changedValue,
      validation: {
        ...formRequest.validation,
        submitLabel: {
          isValid,
          message: formRequest.validation.submitLabel.message,
        },
      },
    });
    return isValid;
  };

  const handleSuccessMessageChange = (e) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, true, 0, 1024);
    setFormRequest({
      ...formRequest,
      successMessage: changedValue,
      validation: {
        ...formRequest.validation,
        successMessage: {
          isValid,
          message: formRequest.validation.successMessage.message,
        },
      },
    });
    return isValid;
  };

  const handleErrorMessageChange = (e) => {
    const changedValue = e.target.value;
    const isValid = validateString(changedValue, true, 0, 1024);
    setFormRequest({
      ...formRequest,
      errorMessage: changedValue,
      validation: {
        ...formRequest.validation,
        errorMessage: {
          isValid,
          message: formRequest.validation.errorMessage.message,
        },
      },
    });
    return isValid;
  };

  const handlePrimaryColorChange = (e) => {
    const changedValue = e.hex;
    setFormRequest({
      ...formRequest,
      primaryColor: changedValue,
    });
  };

  return (
    <div>
      <TextField
        required
        fullWidth
        margin="normal"
        label="Title"
        variant="outlined"
        value={formRequest.title}
        onChange={handleTitleChange}
        error={
        !formRequest.validation.title.isValid
          && !!formRequest.validation.title.hasChanged
        }
        helperText={!formRequest.validation.title.isValid
          ? formRequest.validation.title.message
          : 'Title displayed at the top of the form'}
      />
      <TextField
        multiline
        rows={2}
        fullWidth
        margin="normal"
        label="Description"
        variant="outlined"
        value={formRequest.description}
        onChange={handleDescriptionChange}
        error={!formRequest.validation.description.isValid}
        helperText={!formRequest.validation.description.isValid
          ? formRequest.validation.description.message
          : 'Description or instructions for the form, displayed under the title'}
      />
      <TextField
        required
        margin="normal"
        label="Submit Button Text"
        variant="outlined"
        value={formRequest.submitButtonText}
        onChange={handleSubmitButtonTextChange}
        error={
        !formRequest.validation.submitButtonText.isValid
          && !!formRequest.validation.submitButtonText.hasChanged
      }
        helperText={!formRequest.validation.submitButtonText.isValid
          ? formRequest.validation.submitButtonText.message
          : 'Text displayed on the submit button of the form'}
      />
      <TextField
        multiline
        rows={2}
        fullWidth
        margin="normal"
        label="Submit Label"
        variant="outlined"
        value={formRequest.submitLabel}
        onChange={handleSubmitLabelChange}
        error={!formRequest.validation.submitLabel.isValid}
        helperText={!formRequest.validation.submitLabel.isValid
          ? formRequest.validation.submitLabel.message
          : 'Text displayed above the submit button of the form'}
      />
      <TextField
        multiline
        rows={2}
        fullWidth
        margin="normal"
        label="Success Message"
        variant="outlined"
        value={formRequest.successMessage}
        onChange={handleSuccessMessageChange}
        error={!formRequest.validation.successMessage.isValid}
        helperText={!formRequest.validation.successMessage.isValid
          ? formRequest.validation.successMessage.message
          : 'Message to display after a form is successfully submitted. Example: "Thank your for your submission, we will get back to you shortly"'}
      />
      <TextField
        multiline
        rows={2}
        fullWidth
        margin="normal"
        label="Error Message"
        variant="outlined"
        value={formRequest.errorMessage}
        onChange={handleErrorMessageChange}
        error={!formRequest.validation.errorMessage.isValid}
        helperText={!formRequest.validation.errorMessage.isValid
          ? formRequest.validation.errorMessage.message
          : 'Message to display after a form is successfully submitted. Example: "There was an error submitting the form, please contact help@mycompany.com"'}
      />
      <FormControl>
        <ListItem disableGutters>
          <ListItemText
            primary="Primary Color"
            secondary="This is the main color you will see in the form for items like the submit button, loading bar, etc."
          />
          <ListItemSecondaryAction>
            <Tooltip title="Click to change primary color">
              <div
                className={classes.circle}
                style={{ backgroundColor: formRequest.primaryColor }}
                role="presentation"
                onClick={() => { setDisplayColorPicker(true); }}
              />
            </Tooltip>
            {
                displayColorPicker
                && (
                  <div className={classes.colorContainer}>
                    <div
                      className={classes.colorPaperContainer}
                      onClick={() => { setDisplayColorPicker(false); }}
                      onKeyDown={() => { setDisplayColorPicker(false); }}
                      role="presentation"
                    />
                    <Paper>
                      <TwitterPicker
                        color={formRequest.primaryColor}
                        colors={colors}
                        onChange={handlePrimaryColorChange}
                        direction="vertical"
                      />
                    </Paper>
                  </div>
                )
              }
          </ListItemSecondaryAction>
        </ListItem>
      </FormControl>
    </div>
  );
};

FormInformationComponent.propTypes = {
  formRequest: PropTypes.object,
  setFormRequest: PropTypes.func,
};
FormInformationComponent.defaultProps = {
  formRequest: null,
  setFormRequest: () => {},
};

export default FormInformationComponent;
